import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  acceptFeedback,
  completeFeedback,
  FeedbackStatus,
  findFeedbacks,
  FindFeedbacksParams,
  FindFeedbacksResponse,
} from 'src/api/feedback';
import { SortDirection } from 'src/api/types';
import { defaultServerError } from 'src/utils/consts';

import { messagesActions } from '../messages/slice';
import { StoreState, ThunkConfig } from '../types';

import { feedbackFirstPageNumber } from './initialState';
import { getFeedbackState } from './selectors';
import { FeedbackPageMode } from './types';

export const feedbackPageSize = 15;

export const fetchFeedbacks = createAsyncThunk<
  FindFeedbacksResponse | void,
  boolean,
  ThunkConfig<string>
>(
  'feedback/fetchFeedbacks',
  async (fromStart, { dispatch, getState, rejectWithValue, requestId }) => {
    try {
      const state = getState() as StoreState;
      const {
        feedbackType,
        mode,
        hasMore: hasMoreFromState,
        pageNumber: pageNumberFromState,
        currentRequestId,
      } = getFeedbackState(state);

      const hasMore = fromStart ? true : hasMoreFromState;
      const pageNumber = fromStart
        ? feedbackFirstPageNumber
        : pageNumberFromState;

      if (!hasMore || currentRequestId !== requestId) return;

      const actual = mode === FeedbackPageMode.Actual;

      const params: FindFeedbacksParams = {
        pageSize: feedbackPageSize,
        pageNumber,
        typeId: feedbackType,
        status: actual
          ? [FeedbackStatus.Accepted, FeedbackStatus.Created]
          : [FeedbackStatus.Completed],
        sortDirection: actual ? SortDirection.Asc : SortDirection.Desc,
      };

      const response = await findFeedbacks(params);

      return response;
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
  {
    condition: (fromStart, { getState }) => {
      const state = getState() as StoreState;
      const { mode } = getFeedbackState(state);

      if (!mode) return false;
    },
  },
);

export const makeFeedbackAccepted = createAsyncThunk<
  void,
  number,
  ThunkConfig<string>
>(
  'feedback/makeFeedbackAccepted',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await acceptFeedback(id);
      dispatch(fetchFeedbacks(true));
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const makeFeedbackCompleted = createAsyncThunk<
  void,
  number,
  ThunkConfig<string>
>(
  'feedback/makeFeedbackCompleted',
  async (id, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as StoreState;

      const { comment } = getFeedbackState(state);

      await completeFeedback(id, comment);
      dispatch(fetchFeedbacks(true));
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);
